import React, { useEffect, useState, useRef } from "react";
import { makeStyles } from "@material-ui/styles";
import axios from "axios";
import { useReactToPrint } from "react-to-print";
import Loading from "app/components/Loading";
import { Button } from "react-bootstrap";
import { format } from "date-fns";
import { InputLabel } from "@material-ui/core";
import {DateTimePickerOutlined} from "../../../components/DatePicker";
import {GMTDateConvert} from "../../../../helpers";
import dayjs from "dayjs";

const useStyles = makeStyles({
  container: {
    width: "100%",
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "20px",
    gap: "100px",
  },
  filters: {
    display: "flex",
    gap: "8px",
  },
  filtersWrapper: {

  },
  filter: {
    background: "#FFFFFF",
  },
  report: {
    width: "500px",
    padding: "20px",
    background: "#FFFFFF",
    boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
    borderRadius: "8px",
    overflowY: "auto",
    "@media print": {
      width: "100%",
    },
  },
  reportTitle: {
    fontSize: "18px",
    fontWeight: "bold",
    textTransform: "uppercase",
    textAlign: "center",
    "@media print": {
      fontSize: "100px",
      marginBottom: "40px",
    },
  },
  reportDate: {
    fontSize: "14px",
    textAlign: "center",
    "@media print": {
      fontSize: "40px",
    },
  },
  line: {
    width: "100%",
    border: "1px dashed #000000",
    marginBlock: "4px",
    "@media print": {
      borderWidth: "4px",
      marginBottom: "20px",
    },
  },
  text: {
    "@media print": {
      fontSize: "50px",
    },
  },
  order: {
    backgroundColor: "#000000",
    display: "flex",
    justifyContent: "center",
    width: "20px",
    height: "20px",
    paddingInline: "4px",
    borderRadius: "4px",
    color: "#FFFFFF",
    overflow: "hidden",
    marginRight: "4px",
    "@media print": {
      width: "60px",
      height: "80px",
    },
  },
});

const date = new Date();
const yesterday = new Date(date);
yesterday.setDate(date.getDate() - 1);
const fromDate = yesterday.toISOString().split("T")[0] + "T12:00";
const toDate = date.toISOString().split("T")[0] + "T23:59";

export default function DailyReport() {
  const [data, setData] = useState(null);
  const [filterInputs, setFilterInputs] = useState({
    fromDate,
    toDate,
  });
  const [isLoading, setIsLoading] = useState(true);

  const componentRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const classes = useStyles();

  const filterInputsUpdate = (e) => {
    e.persist();
    setFilterInputs((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  useEffect(() => {
    const getReport = () => {
      axios
        .get("reports/agentDailyReport", {
          params: {
            startDate: `${filterInputs.fromDate}`,
            endDate: `${filterInputs.toDate}`,
          },
        })
        .then(({ data }) => {
          setData(data);
        })
        .catch(({ response }) => {
          const err = response?.data?.message
            ? response?.data?.message
            : "An error occurred.";
          console.log(err);
        })
        .finally(() => setIsLoading(false));
    };

    getReport();
  }, [filterInputs]);

  return (
    <div className={classes.container}>
      <div className={classes.filtersWrapper}>
        <h2 className='text-center'>Tarix aralığı seçin</h2>
        <div className={classes.filters}>
          <DateTimePickerOutlined
              value={filterInputs.fromDate ? dayjs(filterInputs.fromDate) : ""}
              onChange={(value) => setFilterInputs(prevState => ({
                ...prevState, fromDate: value
              }))}
          />
          <DateTimePickerOutlined
              value={filterInputs.toDate ? dayjs(filterInputs.toDate) : ""}
              onChange={(value) => setFilterInputs(prevState => ({
                ...prevState, toDate: value
              }))}
          />
        </div>
      </div>
      <div className={classes.report} ref={componentRef}>
        {isLoading ? (
          <Loading />
        ) : (
          <>
            <h1 className={classes.reportTitle}>GÜNLÜK HESABAT</h1>

            <div className={`d-flex justify-content-between ${classes.text}`}>
              <span className="font-weight-bold">Çap tarixi</span>
              <span>{format(new Date(), "dd/MM/yyyy HH:mm:ss")}</span>
            </div>
            <div className={`d-flex justify-content-between ${classes.text}`}>
              <span className="font-weight-bold">Kassir ID</span>
              <span>{data?.agentNumber}</span>
            </div>

            <div className={`d-flex justify-content-between ${classes.text}`}>
              <span className="font-weight-bold">Dövr</span>
              <span>{data?.dateTime}</span>
            </div>

            <div className={classes.line}></div>
            <div className="d-flex justify-content-between">
              <span className={classes.text}>
                <b>Satışlar</b>
              </span>
              <span className={classes.text}>
                {data?.grossSales?.toFixed(2)}
              </span>
            </div>
            <div className="d-flex justify-content-between">
              <span className={classes.text}>
                <b>Satışlar üzrə kommisiya</b>
              </span>
              <span className={classes.text}>
                {data?.salesCommission?.toFixed(2)}
              </span>
            </div>
            <div className="d-flex justify-content-between">
              <span className={classes.text}>
                <b>Ləğv edilənlər</b>
              </span>
              <span className={classes.text}>{data?.paidRefunds}</span>
            </div>
            <div className="d-flex justify-content-between">
              <span className={classes.text}>
                <b>Ləğv edilənlər üzrə kommisiya</b>
              </span>
              <span className={classes.text}>{data?.refundableCommission}</span>
            </div>
            <div className="d-flex justify-content-between">
              <span className={classes.text}>
                <b>Ödənişlər</b>
              </span>
              <span className={classes.text}>{data?.netPayments}</span>
            </div>
            <div className="d-flex justify-content-between">
              <span className={classes.text}>
                <b>Ödənişlər üzrə kommisiya</b>
              </span>
              <span className={classes.text}>{data?.commissionOnPayments}</span>
            </div>
            <div className="d-flex justify-content-between">
              <span className={classes.text}>
                <b>Aralıq ödəniş</b>
              </span>
              <span className={classes.text}>{data?.interimPayments}</span>
            </div>

            <div className="d-flex justify-content-between">
              <span className={classes.text}>
                <b>Debit/Kredit</b>
              </span>
              <span className={classes.text}>
                {data?.debitCreditBalance}
              </span>
            </div>
          </>
        )}
      </div>
      <Button onClick={handlePrint}>Çap et</Button>
    </div>
  );
}
